import { getDocs, collection, doc, updateDoc, addDoc, deleteDoc, query, orderBy, Timestamp, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';

export interface Protocol {
    id: string;
    type: 'Receiving' | 'Dispatching';
    locationType: 'Hotel' | 'Airport';
    dateTime: string;            // Pick or drop date time (ISO string or datetime-local format)
    address: string;             // Pinned or typed address
    latitude: string;            // Pinpoint coordinate
    longitude: string;           // Pinpoint coordinate
    reachTime: string;           // Datetime when officer reaches destination
    remarks: string;             // General remarks
    createdBy: string;           // Name/email of creator
    createdByPsoId?: string;     // PSO authUid of creator
    createdAt: any;              // Timestamp
}

export async function fetchProtocols(bankId: string): Promise<Protocol[]> {
    if (!bankId) return [];
    
    const protocolsRef = collection(db, 'banks', bankId, 'protocols');
    const q = query(protocolsRef, orderBy('createdAt', 'desc'));
    const snap = await getDocs(q);
    
    return snap.docs.map(d => {
        const data = d.data();
        return {
            id: d.id,
            type: data.type || 'Receiving',
            locationType: data.locationType || 'Airport',
            dateTime: data.dateTime || '',
            address: data.address || '',
            latitude: data.latitude || '',
            longitude: data.longitude || '',
            reachTime: data.reachTime || '',
            remarks: data.remarks || '',
            createdBy: data.createdBy || '',
            createdByPsoId: data.createdByPsoId || '',
            createdAt: data.createdAt,
        } as Protocol;
    });
}

/**
 * Fetch the authUids of all PSOs managed by a Regional Head / Regional PSO.
 * @param bankId   - The ACTUAL Firestore document ID of the bank (not companyId field).
 * @param regionalPsoDocId - The ACTUAL Firestore document ID of the regional head's pos doc.
 */
export async function fetchManagedPsoIds(bankId: string, regionalPsoDocId: string): Promise<string[]> {
    if (!bankId || !regionalPsoDocId) return [];
    try {
        // Fetch the managed PSOs subcollection using the exact doc ID path
        const managedSnap = await getDocs(collection(db, 'banks', bankId, 'pos', regionalPsoDocId, 'managedPsos'));
        const managedPsoDocIds = managedSnap.docs
            .map(docSnap => docSnap.data()?.psoId as string | undefined)
            .filter((id): id is string => Boolean(id));

        if (managedPsoDocIds.length === 0) return [];

        // For each managed PSO doc ID, fetch their authUid (and include doc ID as fallback)
        const expandedIds = new Set<string>();
        for (const psoDocId of managedPsoDocIds) {
            expandedIds.add(psoDocId); // Include doc ID as fallback
            const psoDoc = await getDoc(doc(db, 'banks', bankId, 'pos', psoDocId));
            if (psoDoc.exists()) {
                const authUid = psoDoc.data()?.authUid;
                if (authUid) expandedIds.add(authUid);
                const psoIdField = psoDoc.data()?.psoId;
                if (psoIdField) expandedIds.add(psoIdField);
            }
        }

        return Array.from(expandedIds);
    } catch (error) {
        console.error('Error fetching managed PSO IDs:', error);
        return [];
    }
}

export async function addProtocol(bankId: string, data: Omit<Protocol, 'id' | 'createdAt'>): Promise<string> {
    if (!bankId) throw new Error('Bank ID is required to create a protocol');
    
    const protocolsRef = collection(db, 'banks', bankId, 'protocols');
    const docRef = await addDoc(protocolsRef, {
        ...data,
        createdAt: Timestamp.now()
    });
    
    return docRef.id;
}

export async function updateProtocol(bankId: string, id: string, data: Partial<Protocol>): Promise<void> {
    if (!bankId || !id) throw new Error('Bank ID and Protocol ID are required to update');
    
    const docRef = doc(db, 'banks', bankId, 'protocols', id);
    await updateDoc(docRef, {
        ...data,
        updatedAt: Timestamp.now()
    });
}

export async function deleteProtocol(bankId: string, id: string): Promise<void> {
    if (!bankId || !id) throw new Error('Bank ID and Protocol ID are required to delete');
    
    const docRef = doc(db, 'banks', bankId, 'protocols', id);
    await deleteDoc(docRef);
}
